import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

interface HeaderProps {
  onToggleSidebar: () => void;
  onToggleSourcePanel: () => void;
  sidebarOpen: boolean;
  sourcePanelOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  onToggleSourcePanel,
  sourcePanelOpen,
}) => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = React.useState(false);

  // Check admin status on mount
  React.useEffect(() => {
    const checkAdmin = async () => {
      const user = await authService.verifyToken();
      setIsAdmin(!!user);
    };
    checkAdmin();
  }, []);

  const handleAdminClick = () => {
    if (isAdmin) {
      navigate('/admin/franconnect');
    } else {
      navigate('/admin/login');
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#FFFFFF',
        color: '#212121',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        borderBottom: '1px solid #E0E0E0',
      }}
    >
      <Toolbar sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Tooltip title="Toggle Chat History">
            <IconButton
              color="inherit"
              onClick={onToggleSidebar}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* BurnGPT Logo */}
            <Box
              component="img"
              src="/burngpt_logo.png"
              alt="BurnGPT"
              sx={{
                height: 40,
                width: 40,
                borderRadius: '50%',
                objectFit: 'cover',
                objectPosition: '45% center',
              }}
            />
          </Box>
        </Box>

        {/* Center Title */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              color: '#1E293B',
              fontSize: '1.25rem',
              fontFamily: '"Outfit", sans-serif',
            }}
          >
            BurnGPT
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Toggle Source Panel">
            <IconButton
              color="inherit"
              onClick={onToggleSourcePanel}
              sx={{
                backgroundColor: sourcePanelOpen ? '#00ACDD' : 'transparent',
                color: sourcePanelOpen ? '#FFFFFF' : '#64748B',
                '&:hover': {
                  backgroundColor: sourcePanelOpen ? '#0078A0' : '#F1F5F9',
                },
              }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
          
          {/* Admin Panel Link - Only show icon, login required */}
          <Tooltip title={isAdmin ? "Admin Panel" : "Admin Login"}>
            <IconButton
              onClick={handleAdminClick}
              sx={{
                color: isAdmin ? '#16A349' : '#757575',
                '&:hover': {
                  backgroundColor: isAdmin ? 'rgba(22, 163, 73, 0.1)' : '#F1F5F9',
                },
              }}
            >
              <AdminIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
